// admin-ui/src/pages/OrdersPage.tsx
// ALL RIGHTS RESERVED
//
// UPDATED 2026-02-09: Full visual polish — migrated from raw CSS to Tailwind
// design system matching the rest of admin-ui (Layout, GovernancePage, etc.)
// Fixed: missing padding/margin, bland styling, uniform font sizes.

/**
 * Exchange Orders Management Page
 * Admin interface for viewing, filtering, and managing exchange orders
 */

import { useState, useCallback } from 'react';
import { useAdminOrders, useExchangeStats } from '../hooks/useExchangeAdmin';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface OrderFilters {
  status?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  side?: string;
  minAmount?: string;
  maxAmount?: string;
}

type OrderStatus = 'pending' | 'submitted' | 'filled' | 'settled' | 'failed' | 'cancelled';

interface Order {
  id: string;
  userId: string;
  side: 'buy' | 'sell';
  status: OrderStatus;
  amountUsd: string;
  amountCxxr: string;
  feeAmount: string;
  netAmount: string;
  filledPrice: string | null;
  failureReason: string | null;
  createdAt: string;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function getStatusClasses(status: OrderStatus): string {
  switch (status) {
    case 'pending':   return 'bg-warning-subtle text-warning-emphasis';
    case 'submitted': return 'bg-warning-subtle text-warning-emphasis';
    case 'filled':    return 'bg-info-subtle text-info-emphasis';
    case 'settled':   return 'bg-success-subtle text-success-emphasis';
    case 'failed':    return 'bg-error-subtle text-error-emphasis';
    case 'cancelled': return 'bg-surface-emphasis text-content-muted';
    default:          return 'bg-surface-emphasis text-content-muted';
  }
}

function getSideClasses(side: 'buy' | 'sell'): string {
  return side === 'buy'
    ? 'bg-success-subtle text-success-emphasis'
    : 'bg-info-subtle text-info-emphasis';
}

// ═══════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════

function SummaryCard({
  label,
  value,
  subtitle,
  accent,
}: {
  label: string;
  value: string;
  subtitle?: string;
  accent?: 'success' | 'warning' | 'error' | 'info' | 'default';
}) {
  const borderColor = {
    success: 'border-t-success',
    warning: 'border-t-warning',
    error:   'border-t-error',
    info:    'border-t-info',
    default: 'border-t-brand-500',
  }[accent || 'default'];

  return (
    <div className={`bg-surface rounded-xl border border-edge shadow-sm ${borderColor} border-t-[3px] p-5`}>
      <p className="text-2xs uppercase tracking-wider font-medium text-content-muted mb-1">{label}</p>
      <p className="text-2xl font-bold text-content">{value}</p>
      {subtitle && (
        <p className="text-xs text-content-secondary mt-1">{subtitle}</p>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: OrderStatus }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClasses(status)}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${
        status === 'settled' ? 'bg-success' :
        status === 'failed' ? 'bg-error' :
        status === 'pending' || status === 'submitted' ? 'bg-warning' :
        status === 'filled' ? 'bg-info' :
        'bg-content-muted'
      }`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function SideBadge({ side }: { side: 'buy' | 'sell' }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-2xs font-semibold uppercase tracking-wide ${getSideClasses(side)}`}>
      {side}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-12 h-12 rounded-full bg-surface-muted flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
        </svg>
      </div>
      <h3 className="text-base font-semibold text-content mb-1">No orders found</h3>
      <p className="text-sm text-content-secondary">Try adjusting your filters or check back later.</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export function OrdersPage() {
  const [filters, setFilters] = useState<OrderFilters>({});
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [page, setPage] = useState(0);
  const limit = 50;

  const { stats } = useExchangeStats('24h');
  const { orders, total, isLoading, cancelOrder, retrySettlement, refetch } = useAdminOrders({
    ...filters,
    limit,
    offset: page * limit,
  });

  const handleFilterChange = useCallback((key: keyof OrderFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
    setPage(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setPage(0);
  }, []);

  const handleCancelOrder = useCallback(async () => {
    if (!selectedOrder || !cancelReason.trim()) return;

    try {
      await cancelOrder(selectedOrder.id, cancelReason);
      setShowCancelModal(false);
      setCancelReason('');
      setSelectedOrder(null);
      refetch();
    } catch (error) {
      alert(`Failed to cancel order: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [selectedOrder, cancelReason, cancelOrder, refetch]);

  const handleRetrySettlement = useCallback(async (order: Order) => {
    if (!confirm(`Retry settlement for order ${order.id}?`)) return;

    try {
      await retrySettlement(order.id);
      refetch();
    } catch (error) {
      alert(`Failed to retry settlement: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [retrySettlement, refetch]);

  const handleExportCSV = useCallback(() => {
    const headers = ['Order ID', 'User ID', 'Side', 'Status', 'Amount USD', 'Amount CXXR', 'Fee', 'Net', 'Created'];
    const rows = orders.map(o => [
      o.id,
      o.userId,
      o.side,
      o.status,
      o.amountUsd,
      o.amountCxxr,
      o.feeAmount,
      o.netAmount,
      o.createdAt,
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exchange-orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [orders]);

  const totalPages = Math.ceil(total / limit);
  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div className="p-6 max-w-[1440px] mx-auto">
      {/* ─── Page Header ─── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-content">Exchange Orders</h1>
          <p className="text-sm text-content-secondary mt-0.5">
            Monitor and manage exchange order activity
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-edge bg-surface text-content hover:bg-surface-muted transition-colors"
          >
            Export CSV
          </button>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* ─── Summary Cards ─── */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <SummaryCard
            label="Volume (24h)"
            value={formatCurrency(stats.volume)}
            subtitle={`${stats.orderCount} orders`}
            accent="default"
          />
          <SummaryCard
            label="Buy Orders"
            value={stats.buyCount.toString()}
            subtitle={formatCurrency(stats.buyVolume)}
            accent="success"
          />
          <SummaryCard
            label="Sell Orders"
            value={stats.sellCount.toString()}
            subtitle={formatCurrency(stats.sellVolume)}
            accent="info"
          />
          <SummaryCard
            label="Pending"
            value={stats.pendingCount.toString()}
            accent={stats.pendingCount > 10 ? 'warning' : 'default'}
          />
          <SummaryCard
            label="Failed (24h)"
            value={stats.failedCount.toString()}
            accent={stats.failedCount > 0 ? 'error' : 'default'}
          />
        </div>
      )}

      {/* ─── Filters ─── */}
      <div className="rounded-lg border border-edge bg-surface-subtle p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-edge bg-surface text-content focus:outline-none focus:border-edge-focus focus:ring-1 focus:ring-edge-focus min-w-[140px]"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="filled">Filled</option>
            <option value="settled">Settled</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={filters.side || ''}
            onChange={(e) => handleFilterChange('side', e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-edge bg-surface text-content focus:outline-none focus:border-edge-focus focus:ring-1 focus:ring-edge-focus min-w-[120px]"
          >
            <option value="">All Sides</option>
            <option value="buy">Buy</option>
            <option value="sell">Sell</option>
          </select>

          <input
            type="text"
            placeholder="User ID"
            value={filters.userId || ''}
            onChange={(e) => handleFilterChange('userId', e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-edge bg-surface text-content placeholder:text-content-subtle focus:outline-none focus:border-edge-focus focus:ring-1 focus:ring-edge-focus w-40"
          />

          <input
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-edge bg-surface text-content focus:outline-none focus:border-edge-focus focus:ring-1 focus:ring-edge-focus"
          />

          <input
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-edge bg-surface text-content focus:outline-none focus:border-edge-focus focus:ring-1 focus:ring-edge-focus"
          />

          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm font-medium text-content-muted hover:text-content transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* ─── Orders Table ─── */}
      <div className="rounded-xl border border-edge bg-surface shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-8 h-8 border-2 border-edge border-t-brand-500 rounded-full animate-spin" />
            <p className="text-sm text-content-secondary">Loading orders…</p>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge bg-surface-subtle">
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">Order ID</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">User</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">Side</th>
                    <th className="px-5 py-3 text-right text-2xs uppercase tracking-wider font-semibold text-content-muted">Amount</th>
                    <th className="px-5 py-3 text-right text-2xs uppercase tracking-wider font-semibold text-content-muted">Fee</th>
                    <th className="px-5 py-3 text-right text-2xs uppercase tracking-wider font-semibold text-content-muted">Net</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">Status</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">Created</th>
                    <th className="px-5 py-3 text-right text-2xs uppercase tracking-wider font-semibold text-content-muted">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge-subtle">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-surface-subtle/50 transition-colors">
                      <td className="px-5 py-3.5 text-xs font-mono text-content-secondary">{order.id.slice(-12)}</td>
                      <td className="px-5 py-3.5 text-xs font-mono text-content-secondary">{order.userId}</td>
                      <td className="px-5 py-3.5">
                        <SideBadge side={order.side} />
                      </td>
                      <td className="px-5 py-3.5 text-sm text-content text-right tabular-nums">
                        {order.side === 'buy'
                          ? formatCurrency(order.amountUsd)
                          : `${parseFloat(order.amountCxxr).toFixed(2)} CXXR`
                        }
                      </td>
                      <td className="px-5 py-3.5 text-sm text-content-secondary text-right tabular-nums">{formatCurrency(order.feeAmount)}</td>
                      <td className="px-5 py-3.5 text-sm text-content text-right tabular-nums">
                        {order.side === 'buy'
                          ? `${parseFloat(order.netAmount).toFixed(2)} CXXR`
                          : formatCurrency(order.netAmount)
                        }
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="px-5 py-3.5 text-xs text-content-muted whitespace-nowrap">{formatDate(order.createdAt)}</td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            className="px-2 py-1 text-xs font-medium text-brand-500 hover:bg-brand-50 rounded transition-colors"
                            onClick={() => setSelectedOrder(order)}
                          >
                            View
                          </button>
                          {order.status === 'pending' && (
                            <button
                              className="px-2 py-1 text-xs font-medium text-error hover:bg-error-subtle rounded transition-colors"
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowCancelModal(true);
                              }}
                            >
                              Cancel
                            </button>
                          )}
                          {order.status === 'failed' && (
                            <button
                              className="px-2 py-1 text-xs font-medium text-warning hover:bg-warning-subtle rounded transition-colors"
                              onClick={() => handleRetrySettlement(order)}
                            >
                              Retry
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-edge bg-surface-subtle/50">
              <span className="text-xs text-content-muted">
                Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-edge bg-surface text-content hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-content-muted px-2">
                  Page {page + 1} of {totalPages || 1}
                </span>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-edge bg-surface text-content hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ─── Order Detail Modal ─── */}
      {selectedOrder && !showCancelModal && (
        <div
          className="fixed inset-0 z-modal-backdrop bg-black/40 flex items-center justify-center p-4"
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="bg-surface rounded-2xl shadow-xl border border-edge max-w-lg w-full max-h-[90vh] overflow-y-auto z-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-edge">
              <h2 className="text-lg font-semibold text-content">Order Details</h2>
            </div>

            <div className="px-6 py-4 space-y-0 divide-y divide-edge-subtle">
              <DetailRow label="Order ID" mono>{selectedOrder.id}</DetailRow>
              <DetailRow label="User ID" mono>{selectedOrder.userId}</DetailRow>
              <DetailRow label="Type">{selectedOrder.side === 'buy' ? 'Buy CXXR' : 'Sell CXXR'}</DetailRow>
              <DetailRow label="Status"><StatusBadge status={selectedOrder.status} /></DetailRow>
              <DetailRow label="Amount USD">{formatCurrency(selectedOrder.amountUsd)}</DetailRow>
              <DetailRow label="Amount CXXR">{parseFloat(selectedOrder.amountCxxr).toFixed(2)} CXXR</DetailRow>
              <DetailRow label="Fee">{formatCurrency(selectedOrder.feeAmount)}</DetailRow>
              <DetailRow label="Net Amount">{formatCurrency(selectedOrder.netAmount)}</DetailRow>
              <DetailRow label="Created">{formatDate(selectedOrder.createdAt)}</DetailRow>
              {selectedOrder.failureReason && (
                <DetailRow label="Failure Reason" error>{selectedOrder.failureReason}</DetailRow>
              )}
            </div>

            <div className="px-6 py-4 border-t border-edge flex justify-end">
              <button
                onClick={() => setSelectedOrder(null)}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-edge bg-surface text-content hover:bg-surface-muted transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Cancel Modal ─── */}
      {showCancelModal && selectedOrder && (
        <div
          className="fixed inset-0 z-modal-backdrop bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowCancelModal(false)}
        >
          <div
            className="bg-surface rounded-2xl shadow-xl border border-edge max-w-md w-full z-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-edge">
              <h2 className="text-lg font-semibold text-content">Cancel Order</h2>
              <p className="text-sm text-content-secondary mt-1 font-mono">
                {selectedOrder.id}
              </p>
            </div>

            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-content mb-2">
                Cancellation Reason
              </label>
              <textarea
                placeholder="Enter reason for cancellation…"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg border border-edge bg-surface text-content placeholder:text-content-subtle resize-vertical focus:outline-none focus:border-edge-focus focus:ring-1 focus:ring-edge-focus"
              />
            </div>

            <div className="px-6 py-4 border-t border-edge flex justify-end gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-edge bg-surface text-content hover:bg-surface-muted transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={!cancelReason.trim()}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-error text-white hover:bg-error-emphasis disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// DETAIL ROW (for modals)
// ═══════════════════════════════════════════════════════

function DetailRow({
  label,
  children,
  mono,
  error,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  error?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="text-sm text-content-secondary">{label}</span>
      <span className={`text-sm font-medium ${mono ? 'font-mono text-xs' : ''} ${error ? 'text-error' : 'text-content'}`}>
        {children}
      </span>
    </div>
  );
}

export default OrdersPage;
